/**
 * Single source of truth for the Functional LAB pricing catalogue that lives
 * on BOTH `/prices` and the Service Finder result screen.
 *
 * Rules:
 *   - Prices, durations, inclusions and deep-link anchors defined here are
 *     the authoritative values. Any component that renders a service, package
 *     or membership must import from this file — no re-hardcoding.
 *   - Membership rows carry `fallback` values that are only shown when the
 *     live `/api/packages` list is unreachable or missing an entry.
 *
 * If you rename or reprice a package, this is the only file to change.
 */

export const REHAB_PACKAGES = [
  {
    id: "recover",
    name: "Recovery Package",
    duration: "4–6 weeks",
    full: 299,
    weekly_n: 4,
    weekly_amt: 87.5,
    save: 51,
    anchor: "/prices#packages",
    sub: "For recent injuries, recurring pain or a smooth return to normal daily activities.",
    feat: [
      "Comprehensive injury and movement assessment",
      "4 one-to-one rehabilitation sessions",
      "2 online rehabilitation check-ins",
      "Personalised exercise programme in the app",
      "One sports massage or recovery treatment",
      "Final reassessment and progress review",
    ],
  },
  {
    id: "return-to-performance",
    name: "Return to Performance",
    duration: "8–10 weeks",
    popular: true,
    full: 499,
    weekly_n: 4,
    weekly_amt: 142.0,
    save: 69,
    anchor: "/prices#packages",
    sub: "Return safely to running, gym, football or sport with strength and confidence.",
    feat: [
      "Injury, movement and performance assessment",
      "8 one-to-one clinic, gym or sport-specific sessions",
      "4 online rehabilitation and progress reviews",
      "Running / jumping / change-of-direction progression",
      "Strength, mobility and functional testing",
      "Two sports massage or recovery treatments",
    ],
  },
  {
    id: "surgery-recovery",
    name: "Surgery Recovery",
    duration: "12–16 weeks",
    full: 799,
    weekly_n: 4,
    weekly_amt: 222.5,
    save: 91,
    anchor: "/prices#packages",
    sub: "Structured pre- and post-op rehabilitation guided phase-by-phase.",
    feat: [
      "Pre- or post-operative assessment",
      "12 one-to-one rehabilitation sessions",
      "6 online rehabilitation and progress reviews",
      "Phase-by-phase surgical rehabilitation plan",
      "Two massage or recovery treatments (when clinically appropriate)",
      "Communication with your surgeon or consultant when needed",
    ],
    notice:
      "Massage and manual therapy will only be provided when safe and clinically appropriate following surgery.",
  },
  {
    id: "signature-programme",
    name: "Functional LAB Signature Programme",
    duration: "12 weeks",
    full: 999,
    weekly_n: 4,
    weekly_amt: 262.5,
    save: 51,
    anchor: "/prices#packages",
    sub: "Our most comprehensive experience — continuous rehab, recovery and performance support.",
    feat: [
      "Full injury, movement and performance consultation",
      "Unlimited clinically-appropriate 1:1 sessions*",
      "Unlimited online rehabilitation check-ins*",
      "Unlimited massage and recovery treatments (when clinically appropriate)*",
      "Priority booking and priority messaging",
      "Mid- and end-of-programme performance testing",
      "Long-term training and injury-prevention plan",
    ],
    notice:
      "* Subject to clinical recommendation, practitioner availability and fair-use conditions. Appointments cannot be carried beyond the 12-week programme.",
  },
];

/** Fallback membership catalogue — mirrors what the /api/packages endpoint
 * returns. Used when the API is unreachable OR when the Finder needs metadata
 * (inclusions, alt suggestions) the API doesn't carry. Live API values always
 * win when available. Kept in sync with the seed in
 * `/app/backend/server.py::_seed_catalogue_and_packages`. */
export const FALLBACK_MEMBERSHIPS = [
  {
    id: "digital",
    name: "Digital Support Membership",
    price: 59,
    billing: "month",
    anchor: "/prices#memberships",
    sub: "Flexible remote support to help you follow, maintain and progress your personalised exercise programme.",
    feat: [
      "Fully remote",
      "Personalised exercise programme",
      "Monthly progress review",
      "Monthly programme update",
      "Messaging support",
    ],
    remoteOnly: true,
  },
  {
    id: "essential",
    name: "Essential Membership",
    price: 89,
    billing: "month",
    anchor: "/prices#memberships",
    sub: "Begin with a comprehensive face-to-face consultation, then continue through guided online rehabilitation and monthly clinician reviews.",
    feat: [
      "Initial face-to-face consultation",
      "Personalised rehabilitation plan",
      "Monthly video review",
      "Exercise-technique feedback",
      "Ongoing remote support",
    ],
  },
  {
    id: "progress",
    name: "Progress Membership",
    price: 169,
    billing: "month",
    anchor: "/prices#memberships",
    sub: "Combine a monthly clinic, gym or online session with continued remote monitoring, programme progression and technique feedback.",
    feat: [
      "One monthly 45-minute session",
      "One additional online review",
      "Regular programme progression",
      "Exercise-video feedback",
      "Weekly monitoring",
    ],
    popular: true,
  },
  {
    id: "performance",
    name: "Performance Membership",
    price: 279,
    billing: "month",
    anchor: "/prices#memberships",
    sub: "Higher-contact rehabilitation and performance support for returning to running, demanding training or sport.",
    feat: [
      "Two monthly supervised sessions",
      "Performance programming",
      "Weekly workload monitoring",
      "Quarterly objective testing",
      "Priority support and booking",
    ],
  },
];

/** À la carte session references — one-off individual sessions. */
export const ONE_OFF_SESSIONS = {
  initial: {
    id: "initial-assessment",
    name: "Initial Assessment (60 min)",
    price: 95,
    duration: "60 min",
    anchor: "/services",
    sub: "The safest place to start when you're unsure — a clinician will review your goals and outline the right pathway.",
    feat: [
      "Full injury and movement screen",
      "Assessment findings and prognosis",
      "Written action plan and recommendations",
    ],
  },
  followUp: {
    id: "follow-up",
    name: "Follow-Up Session",
    price: 75,
    duration: "45 min",
    anchor: "/services",
    sub: "One-off treatment, review or check-in session.",
    feat: [
      "Progress review and adjustments",
      "Hands-on treatment or exercise coaching",
      "Programme update in the app",
    ],
  },
  teleRehab: {
    id: "tele-rehab",
    name: "Tele-Rehabilitation Consultation",
    price: 40,
    duration: "30 min",
    anchor: "/services",
    sub: "A one-off remote consultation with a clinician to review your situation and outline next steps.",
    feat: [
      "Symptom and history review",
      "Advice on immediate next steps",
      "Optional starter exercise plan",
    ],
    remote: true,
  },
};

/** Lookup helper — takes a catalogue id and returns the resolved entry, or null. */
export function findEntry(id) {
  return (
    REHAB_PACKAGES.find((p) => p.id === id) ||
    FALLBACK_MEMBERSHIPS.find((m) => m.id === id) ||
    Object.values(ONE_OFF_SESSIONS).find((s) => s.id === id) ||
    null
  );
}
